/**
 * QR Generator — apps.leferi.com/qr
 *
 * 링크(또는 임의 텍스트)를 입력하면 클라이언트에서 즉시 QR 코드를 생성한다. 외부 API를
 * 쓰지 않고(내부 링크가 외부로 새지 않게) `qrcode` 로 로컬 렌더 → PNG·SVG 다운로드.
 *
 * chrome 은 PdfCompressor 미러링 — 사각 모서리, 핑크 CTA, brown ink, ▪ 마커, no emoji (LCMD).
 */
import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, Download, Link as LinkIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import QRCode from 'qrcode'
import type { AuthSession } from './auth'

type Ecc = 'L' | 'M' | 'Q' | 'H'

const ECC_OPTIONS: { value: Ecc; label: string; hint: string }[] = [
  { value: 'M', label: 'M', hint: '표준 · 균형 (기본)' },
  { value: 'Q', label: 'Q', hint: '인쇄·로고 삽입 시 권장' },
  { value: 'H', label: 'H', hint: '최고 내구성 (밀도↑)' },
]

const PNG_SIZE = 1024 // 다운로드 PNG 해상도(고해상). 화면 미리보기는 CSS로 축소.

export function QrGeneratorPage({ session: _session }: { session: AuthSession }) {
  const [text, setText] = useState('')
  const [ecc, setEcc] = useState<Ecc>('M')
  const [pngUrl, setPngUrl] = useState('')
  const [svg, setSvg] = useState('')
  const [error, setError] = useState<string | null>(null)

  const trimmed = text.trim()

  useEffect(() => {
    if (!trimmed) {
      setPngUrl('')
      setSvg('')
      setError(null)
      return
    }
    let cancelled = false
    const opts = { errorCorrectionLevel: ecc, margin: 2 }
    Promise.all([
      QRCode.toDataURL(trimmed, { ...opts, width: PNG_SIZE }),
      QRCode.toString(trimmed, { ...opts, type: 'svg' }),
    ])
      .then(([png, svgStr]) => {
        if (cancelled) return
        setPngUrl(png)
        setSvg(svgStr)
        setError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setPngUrl('')
        setSvg('')
        setError(e instanceof Error ? e.message : 'QR 생성 실패 (내용이 너무 길 수 있어요)')
      })
    return () => {
      cancelled = true
    }
  }, [trimmed, ecc])

  const svgUrl = useMemo(
    () => (svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : ''),
    [svg],
  )
  const fileBase = useMemo(() => filenameFor(trimmed), [trimmed])

  return (
    <div className="pt-s-7">
      <Link
        to="/"
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Apps
      </Link>

      <div className="mt-s-6">
        <p className="lf-eyebrow">▪ QR Generator · QR 코드 생성</p>
        <h1 className="mt-s-2 lf-display-2">Turn a link into a QR.</h1>
        <p className="mt-s-3 max-w-[640px] lf-body text-fg-2">
          링크를 붙여넣으면 바로 QR 코드가 만들어져요. PNG 또는 SVG로 내려받아 인쇄물·화면에
          쓰세요. 생성은 브라우저 안에서 일어나고 <span className="text-fg-1">외부로 전송되지 않아요.</span>
        </p>
      </div>

      <div className="mt-s-7 grid grid-cols-1 gap-s-6 border border-line-hairline p-s-6 lg:grid-cols-[1fr_minmax(280px,320px)]">
        {/* 입력 */}
        <div className="flex flex-col gap-s-6">
          <Field label="Link · 링크 또는 텍스트">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="https://apps.leferi.com/…"
              autoFocus
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-3 lf-body text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
            />
          </Field>

          <Field label="Error correction · 오류 복원 강도" hint="높을수록 훼손·로고에 강하지만 코드가 촘촘해져요.">
            <div className="flex flex-wrap gap-s-2">
              {ECC_OPTIONS.map((o) => {
                const selected = ecc === o.value
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setEcc(o.value)}
                    className={`flex items-center gap-s-2 rounded-r-1 border px-s-3 py-s-2 text-left transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus ${
                      selected
                        ? 'border-brand-pink bg-pink-50'
                        : 'border-line-hairline bg-surface-base hover:border-line-soft'
                    }`}
                  >
                    <span className="lf-h4 lf-num text-fg-1">{o.label}</span>
                    <span className="lf-caption text-fg-2">{o.hint}</span>
                  </button>
                )
              })}
            </div>
          </Field>

          {error ? <p className="lf-body-sm text-status-danger">▪ {error}</p> : null}
        </div>

        {/* 미리보기 + 다운로드 */}
        <aside className="flex flex-col items-center gap-s-4">
          <div className="flex aspect-square w-full max-w-[320px] items-center justify-center border border-line-hairline bg-white p-s-5">
            {pngUrl ? (
              <img src={pngUrl} alt="생성된 QR 코드" className="h-full w-full object-contain" />
            ) : (
              <div className="flex flex-col items-center gap-s-2 text-center">
                <LinkIcon size={28} strokeWidth={1.4} className="text-fg-3" />
                <p className="lf-body-sm text-fg-3">링크를 입력하면 여기에 QR이 나타나요.</p>
              </div>
            )}
          </div>

          <div className="flex w-full max-w-[320px] gap-s-3">
            <a
              href={pngUrl || undefined}
              download={pngUrl ? `${fileBase}.png` : undefined}
              aria-disabled={!pngUrl}
              className={`inline-flex flex-1 items-center justify-center gap-s-2 rounded-r-2 px-s-4 py-s-3 lf-h4 transition-colors duration-base ease-standard ${
                pngUrl
                  ? 'bg-brand-pink text-brand-brown hover:bg-pink-500'
                  : 'pointer-events-none cursor-not-allowed bg-surface-soft text-fg-3'
              }`}
            >
              <Download size={16} strokeWidth={1.75} />
              PNG
            </a>
            <a
              href={svgUrl || undefined}
              download={svgUrl ? `${fileBase}.svg` : undefined}
              aria-disabled={!svgUrl}
              className={`inline-flex flex-1 items-center justify-center gap-s-2 rounded-r-2 border px-s-4 py-s-3 lf-h4 transition-colors duration-base ease-standard ${
                svgUrl
                  ? 'border-line-soft bg-surface-base text-fg-1 hover:border-line-strong'
                  : 'pointer-events-none cursor-not-allowed border-line-hairline bg-surface-soft text-fg-3'
              }`}
            >
              <Download size={16} strokeWidth={1.75} />
              SVG
            </a>
          </div>
          <p className="lf-eyebrow text-fg-3">▪ 벡터(SVG)는 크기 무제한 · 인쇄용 권장</p>
        </aside>
      </div>
    </div>
  )
}

/** 링크에서 안전한 파일명 베이스 생성 (도메인/경로 일부 → 영숫자). */
function filenameFor(text: string): string {
  if (!text) return 'qr'
  let base = text
  try {
    base = new URL(text).hostname || text
  } catch {
    /* URL 아니면 원문 사용 */
  }
  const slug = base
    .replace(/^https?:\/\//, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  return slug || 'qr'
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="lf-eyebrow text-fg-3">▪ {label}</p>
      <div className="mt-s-3">{children}</div>
      {hint ? <p className="mt-s-2 lf-caption text-fg-3">{hint}</p> : null}
    </div>
  )
}
